import {
  type BannerState,
  bannerState,
  DEFAULT_STOREFRONT_TEMPLATE_ID,
  homeLeadsVisitorsWithGames,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { formatDate } from "../lib/format";
import { useStorefrontConfigQuery } from "../lib/storefront";
import {
  BANNER_STATE_LABELS,
  bannerAudienceLabel,
  moveItem,
  type StorefrontBanner,
} from "../lib/storefrontBanners";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { ErrorAlert } from "./ErrorAlert";
import { DeleteStorefrontBannerButton, StorefrontBannerSheet } from "./StorefrontBannerSheet";

const bannerColumns = createDataTableColumns<StorefrontBanner>();
const BANNER_PINNED = { start: [], end: ["actions"] };

const STATE_BADGE: Record<BannerState, "default" | "outline" | "secondary"> = {
  live: "default",
  scheduled: "outline",
  ended: "secondary",
  paused: "secondary",
};

/** When a banner runs, as the table reads it. */
function bannerWindowLabel(banner: StorefrontBanner): string {
  const from = banner.startsAt === null ? "Right away" : formatDate(banner.startsAt);
  const until = banner.endsAt === null ? "no end" : formatDate(banner.endsAt);
  return `${from} → ${until}`;
}

function buildBannerColumns(args: {
  canEdit: boolean;
  moving: boolean;
  now: Date;
  onMove: (index: number, direction: -1 | 1) => void;
  onOpen: (banner: StorefrontBanner) => void;
}) {
  return bannerColumns.columns([
    bannerColumns.display({
      id: "order",
      header: "Order",
      size: 96,
      cell: ({ row, table }) => {
        const last = table.getCoreRowModel().rows.length - 1;
        return (
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Move ${row.original.name} up`}
              disabled={!args.canEdit || args.moving || row.index === 0}
              onClick={() => args.onMove(row.index, -1)}
            >
              <ArrowUp />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Move ${row.original.name} down`}
              disabled={!args.canEdit || args.moving || row.index === last}
              onClick={() => args.onMove(row.index, 1)}
            >
              <ArrowDown />
            </Button>
          </div>
        );
      },
    }),
    bannerColumns.accessor("name", {
      header: "Name",
      size: 200,
      cell: ({ getValue, row }) => (
        <Button
          type="button"
          variant="link"
          className="h-auto max-w-full justify-start p-0 font-medium text-foreground"
          onClick={() => args.onOpen(row.original)}
        >
          <span className="truncate">{getValue()}</span>
        </Button>
      ),
      meta: { filter: "text" },
    }),
    bannerColumns.accessor((banner) => BANNER_STATE_LABELS[bannerState(banner, args.now)], {
      id: "state",
      header: "Status",
      size: 110,
      cell: ({ row, getValue }) => (
        <Badge variant={STATE_BADGE[bannerState(row.original, args.now)]}>{getValue()}</Badge>
      ),
      meta: { filter: "text" },
    }),
    bannerColumns.accessor((banner) => bannerWindowLabel(banner), {
      id: "runs",
      header: "Runs",
      size: 230,
    }),
    bannerColumns.accessor((banner) => bannerAudienceLabel(banner.spendBands), {
      id: "audience",
      header: "Shown to",
      meta: { filter: "text" },
    }),
    bannerColumns.accessor((banner) => banner.slides.length, {
      id: "slides",
      header: "Slides",
      size: 80,
      meta: { align: "end", filter: "number" },
    }),
    bannerColumns.display({
      id: "actions",
      size: 150,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => args.onOpen(row.original)}
          >
            {args.canEdit ? "Edit" : "View"}
          </Button>
          {args.canEdit && <DeleteStorefrontBannerButton bannerId={row.original.id} size="sm" />}
        </div>
      ),
    }),
  ]);
}

/**
 * The Storefront page's Banners tab: the environment's banners in the order the storefront shows
 * them, how many are live now against the limit, and the editor in a sheet.
 */
export function StorefrontBannersEditor() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const storefront = useStorefrontConfigQuery();
  const listOptions = trpc.storefrontBanners.list.queryOptions();
  const list = useQuery(listOptions);
  // Which banner the editor opened (null = a new one), and a key per open for a fresh draft.
  const [editor, setEditor] = useState<{ key: number; banner: StorefrontBanner | null } | null>(
    null,
  );
  const [open, setOpen] = useState(false);
  const [now] = useState(() => new Date());

  const reorder = useMutation(
    trpc.storefrontBanners.reorder.mutationOptions({
      onSuccess: (banners) =>
        queryClient.setQueryData(listOptions.queryKey, (old) => (old ? { ...old, banners } : old)),
    }),
  );

  const banners = list.data?.banners;
  const columns = useMemo(
    () =>
      buildBannerColumns({
        canEdit,
        moving: reorder.isPending,
        now,
        onMove: (index, direction) => {
          if (!banners) return;
          reorder.mutate({
            bannerIds: moveItem(
              banners.map((banner) => banner.id),
              index,
              direction,
            ),
          });
        },
        onOpen: (banner) => {
          setEditor({ key: Date.now(), banner });
          setOpen(true);
        },
      }),
    [banners, canEdit, now, reorder.isPending, reorder.mutate],
  );

  if (list.error) return <ErrorAlert message={list.error.message} />;
  if (!list.data || !storefront.query.data) return <Skeleton className="h-64" />;

  const { limits, segments } = list.data;
  const liveNow = list.data.banners.filter((banner) => bannerState(banner, now) === "live").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Banners</CardTitle>
        <CardDescription>
          {`Scheduled slides for the storefront, each with art, a title, or both. Up to ${limits.maxLive} can be live at once, and each can be shown to everyone or only to some spend bands.`}
        </CardDescription>
        {canEdit && (
          <CardAction>
            <Button
              type="button"
              onClick={() => {
                setEditor({ key: Date.now(), banner: null });
                setOpen(true);
              }}
            >
              <Plus />
              New banner
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {`${liveNow} of ${limits.maxLive} live now. The storefront shows them in this order.`}
        </p>
        <DataTable
          columns={columns}
          data={list.data.banners}
          getRowId={(banner) => banner.id}
          pinned={BANNER_PINNED}
          search="Search banners"
          csvName="storefront-banners"
          emptyMessage="No banners yet. Add one to feature boxes, drops or offers on the storefront."
        />
      </CardContent>

      <StorefrontBannerSheet
        editorKey={editor?.key ?? null}
        banner={editor?.banner ?? null}
        open={open}
        onOpenChange={setOpen}
        readOnly={!canEdit}
        maxSlides={limits.maxSlides}
        segments={segments}
        uploadsAvailable={storefront.query.data.assetsAvailable}
        visitorsSeeGames={homeLeadsVisitorsWithGames(
          storefront.query.data.config.storefrontTemplate ?? DEFAULT_STOREFRONT_TEMPLATE_ID,
          storefront.query.data.config.pageTemplates ?? {},
        )}
      />
    </Card>
  );
}
